import { useEffect, useState } from 'react';
import { useHistoryWithReferrer } from '@v2/utils/history';

import { IProduct } from '@interfaces';

import Clickable from '@v2/components/common/presentational/clickables/default';
import { mapVeggieBundle } from '@v2/components/pages/shop/utils';
import { useAddAgeBundle, useBuyingForBusiness, useIsSwapping } from '@v2/components/common/products/utils';

import iconQuestion from '@img/shop/question-bundle.svg';
import iconWarning from '@img/shop/Warning.svg';
import { useWarningModal } from '@components/common/presentational/modal/common';
import { useBundleUpgradeModal } from '@v2/components/common/presentational/modal/common/bundle.upgrade';
import { useAgeBundlePDPModal } from '@v2/components/common/presentational/modal/common';
import { setScrollPosition } from '@components/common/helpers/scroll-position';
import { apiImageSource } from '@v2/utils/http';
import { WPImage } from '@v2/utils/image';
import { useGTMItemDispatch } from '@v2/utils/gtm';
import { useEntity } from '@app/store/rtk.entities.selectors';
import { calculateBundleTotal } from '@v2/utils/cart';
import Tooltip from '@v2/components/common/presentational/tooltip/tooltip';
import { scroller } from 'react-scroll';

export default function Bundle({ product, index, disabled, subscribed, filterAge, setFilterAge, isAgeBundle, isVeggie, isBuyAgain, staticPDP }:
    {
        product: IProduct, index: number, disabled: boolean, subscribed?: boolean, filterAge?: any, setFilterAge?: Function,
        isAgeBundle?: boolean, isVeggie?: boolean, isBuyAgain?: boolean, staticPDP?: boolean
    }) {
    const history = useHistoryWithReferrer();
    const gtmDispatch = useGTMItemDispatch();

    product = isVeggie ? mapVeggieBundle(product) : product;

    const box: any = {
        lines: {},
        blueprint: {},
        properties: {
            isVegan: isVeggie,
            productId: product.id
        }
    }

    const ageBundles = useEntity('ageBundles');
    const bundleTotal = calculateBundleTotal(product, true);
    const bundleDiscountTotal = calculateBundleTotal(product);
    const isSwapping = useIsSwapping();

    const [ageLabel, setAgeLabel] = useState(null);

    const onAddBundle = useAddAgeBundle();
    const [showWarningModal] = useWarningModal();
    const [showBundleUpgradeModal] = useBundleUpgradeModal(box, true);
    const [showBundlePDP] = useAgeBundlePDPModal(product, true);
    const {showBuyingForBusinessModal, buyingMoreThanTen} = useBuyingForBusiness();

    const onBundleAdd = (product: IProduct, isVeggie: boolean, subscribed?: boolean) => {
        if (buyingMoreThanTen) {
            showBuyingForBusinessModal();
        } else {
            onAddBundle(product, isVeggie, subscribed);
        }
    }

    useEffect(() => {
        setAgeLabel(null);

        if (filterAge === null || !ageBundles) {
            return;
        }

        const bundles: IProduct[] = Object.values(ageBundles);

        if (!bundles[filterAge]) {
            return;
        }

        bundles[filterAge].products.forEach((p1: IProduct) => {
            product.products.forEach((p2: IProduct) => {
                if (p2.sku === p1.sku) {
                    setAgeLabel(bundles[filterAge].title.replace(' Bundle', ''));
                    return;
                }
            });
        });
    }, [filterAge]);

    const select = () => {
        gtmDispatch('select_item', product);

        if (staticPDP || isSwapping) {
            showBundlePDP();
        } else {
            if (history.location.pathname === '/shop') {
                setScrollPosition('shop');
            }
            history.pushWithReferrer(`/bundle/age/${product.slug}`);
        }
    };

    return (
        <div className={`product ${product.availability === 'outofstock' ? 'out-of-stock' : ''} ${isAgeBundle ? 'age-bundle-product' : ''}`}>
            <div className="product-link horizontal">
                <div style={{ position: 'relative' }}>
                    <Clickable onClick={select}>
                        <div className="image">
                            <WPImage
                                src={product.hash ? apiImageSource(`${isVeggie ? 'veggie-' : ''}bundle/${product.id}/${product.hash}/`) : product.mainImage}
                                alt={product.mainImageAlt || product.title} />
                        </div>
                    </Clickable>
                    {isAgeBundle &&
                        <Clickable className="highlight-action" onClick={() => {
                            const url = new URL(window.location.href);
                            url.searchParams.set('bundle', product.slug);
                            window.history.pushState(null, '', url.toString());
                            scroller.scrollTo('bundles', { offset: -90 });
                            setFilterAge(index)
                        }}>
                            <img src={iconQuestion} alt="" />
                        </Clickable>
                    }
                    {isBuyAgain &&
                        <Clickable className="warning-action" onClick={showWarningModal}>
                            <img src={iconWarning} alt="" />
                        </Clickable>
                    }
                </div>
                {!isBuyAgain
                    &&
                    <>
                        <div className="title">
                            <Clickable onClick={select}>{product.title}</Clickable>
                            <div className="quantity mobile">{product.size} items</div>
                        </div>
                        {(bundleTotal > 0 && bundleDiscountTotal > 0) &&
                                <div className="info">
                                    {subscribed
                                        ?
                                        <>
                                            <span className="regular">${bundleTotal.toFixed(2)}</span>
                                            <Tooltip
                                                showOnClick={true}
                                                className="tooltip"
                                                width={250}
                                                description={`Optional Subscribe & Save for an additional 15% off`}>
                                                <span className="discount">${bundleDiscountTotal.toFixed(2)}</span>
                                            </Tooltip>
                                        </>
                                        :
                                        <span className="default">${bundleTotal.toFixed(2)}</span>
                                    }
                                    <span className="desktop"> | {product.size} items</span>
                                </div>
                        }
                    </>
                }
            </div>
            {isBuyAgain
                ?
                <div className={`action-wrapper`}>
                    <div>
                        <div className="title">
                            {product.title}
                        </div>
                        <div className="quantity mobile">{product.size} items</div>
                        {(bundleTotal > 0 && bundleDiscountTotal > 0) &&
                                <div className="info">
                                    <span className="regular">${bundleTotal.toFixed(2)}</span>
                                    <span className="discount">${bundleDiscountTotal.toFixed(2)}</span>
                                    <span className="desktop"> | {product.size} items</span>
                                </div>
                        }
                    </div>
                    <div className={`action add ${disabled ? 'd-none' : ''}`}>
                        {product?.status !== 'outofstock' &&
                            <button
                                className="button primary blue"
                                onClick={() => onBundleAdd(product, isVeggie)}
                                aria-label={'Add to bag'}
                                disabled={disabled}>
                                {isSwapping ? 'Select' : 'Add to bag'}
                            </button>
                        }
                        <button
                            className="button link blue bundle-picker"
                            onClick={showBundleUpgradeModal}
                            aria-label={'Add to bag'}
                            disabled={disabled}>
                            Choose different age range
                        </button>
                    </div>
                </div>
                :
                <div className={`action add ${disabled ? 'd-none' : ''}`}>
                    <button
                        className="button primary blue"
                        onClick={() => onBundleAdd(product, isVeggie, subscribed)}
                        aria-label={'Add to bag'}
                        disabled={disabled}>
                        {isSwapping ? 'Select' : 'Add to bag'}
                    </button>
                </div>
            }
            <div className="out-of-stock-info">out of stock!</div>
            <div className="recommended-info">Best seller</div>
            {ageLabel && product.availability !== 'outofstock' &&
                <div className="age-bundle-nfo">{ageLabel}</div>
            }
        </div>
    );
}